import { memo } from 'react';
import { Alert, Box, Button, SpaceBetween, Spinner } from '@amzn/awsui-components-core';

import ActionPanelBase from './ActionPanelBase';
import Barometer from '../content-quality/Barometer';
import { BarRaiserEvaluationQuestionItem } from './BarRaiserEvaluationQuestionItem';
import { ReleasePriorityModal } from './ReleasePriorityModal';
import { useBarRaiserEvaluationPanelViewModel } from './BarRaiserEvaluationPanelViewModel';
import styles from './BarRaiserEvaluationPanel.module.scss';

const BarRaiserEvaluationPanel = memo(function BarRaiserEvaluationPanel() {
  const vm = useBarRaiserEvaluationPanelViewModel();

  return (
    <ActionPanelBase title="Bar raiser evaluation">
      {vm.submitScreen.kind === 'submitting' ? (
        // Submitted: the workflow is being polled until it reaches its target state.
        <SpaceBetween size="s" direction="vertical" alignItems="center">
          <Spinner size="normal" />
          <Box color="text-body-secondary">
            {vm.submitScreen.decision === 'publish'
              ? 'Publishing your content…'
              : 'Rejecting your content…'}
          </Box>
        </SpaceBetween>
      ) : vm.submitScreen.kind === 'succeeded' ? (
        // When publish succeeds the panel disappears anyway, so only rejection needs a message.
        vm.submitScreen.decision === 'publish' ? (
          <></>
        ) : (
          <SpaceBetween size="s" direction="vertical">
            <Box variant="h3">Rejected Successfully</Box>
            <Box variant="p">This content has been sent back to the author with your feedback.</Box>
          </SpaceBetween>
        )
      ) : vm.submitScreen.kind === 'failed' ? (
        <SpaceBetween size="s" direction="vertical">
          <Box variant="h3">
            {vm.submitScreen.decision === 'publish' ? 'Publishing Failed' : 'Rejection Failed'}
          </Box>
          <Box variant="p">
            {vm.submitScreen.decision === 'publish'
              ? 'There was an issue publishing your content.'
              : 'There was an issue rejecting your content.'}
          </Box>
          <Button variant="primary" onClick={() => window.location.reload()}>
            Reload Content
          </Button>
        </SpaceBetween>
      ) : vm.isLoading ? (
        <Box textAlign="center" padding="l">
          <Spinner size="normal" />
        </Box>
      ) : vm.hasError ? (
        // Load failure: the questionnaire never arrived, so replace the whole panel.
        <Alert type="error">{vm.errorMessage}</Alert>
      ) : (
        <SpaceBetween size="m" direction="vertical">
          <div>
            <div className={styles.gauge}>
              <Barometer
                score={vm.scoreForGauge}
                margin={{ top: 8, right: 24, bottom: 0, left: 24 }}
              />
              <div className={styles.gaugeScore}>
                {vm.isScoreComplete ? (
                  <>
                    {vm.scoreForGauge}
                    <span className={styles.gaugePercent}>%</span>
                  </>
                ) : (
                  '—'
                )}
              </div>
            </div>
            <div className={styles.scoreCaption}>{vm.qualityScoreCaption}</div>
          </div>

          {/* Submit failure: the loaded questionnaire stays, so show a dismissible inline error. */}
          {vm.errorMessage && (
            <Alert type="error" dismissible onDismiss={vm.dismissError}>
              {vm.errorMessage}
            </Alert>
          )}

          {vm.categories.map((category) => (
            <SpaceBetween key={category.categoryName} size="s" direction="vertical">
              <Box variant="h4">{category.categoryName}</Box>
              {category.questions.map((question) => (
                <BarRaiserEvaluationQuestionItem key={question.sequence} question={question} />
              ))}
            </SpaceBetween>
          ))}

          {vm.showDecisionButtons && (
            <SpaceBetween size="xs" direction="vertical">
              <Button
                variant="primary"
                fullWidth
                disabled={vm.isPublishDisabled}
                onClick={vm.publish}
              >
                Publish
              </Button>
              <Button fullWidth disabled={vm.isRejectDisabled} onClick={vm.reject}>
                Reject
              </Button>
            </SpaceBetween>
          )}

          {vm.isPriorityModalVisible && (
            <ReleasePriorityModal
              options={vm.priorityOptions}
              selectedOption={vm.selectedPriorityOption}
              isConfirmDisabled={vm.isPriorityConfirmDisabled}
              isConfirming={false}
              onChange={vm.setSelectedPriority}
              onDismiss={vm.dismissPriorityModal}
              onConfirm={vm.confirmPublishWithPriority}
            />
          )}
        </SpaceBetween>
      )}
    </ActionPanelBase>
  );
});

export default BarRaiserEvaluationPanel;
